import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Fade from 'react-reveal/Fade';
import { IconButton } from '@mui/material';
import { 
  KeyboardArrowUp, 
  KeyboardArrowDown, 
  Twitter, 
  Facebook, 
  LinkedIn, 
  ArrowBack 
} from '@mui/icons-material';
import { useSite } from '../context/SiteContext';
import Skeleton from 'react-loading-skeleton';
import PageFooter from '../componentParts/footer';
import TopNav from '../componentParts/topNav';

export default function BlogDetailPage() {
  const { blogs, loading } = useSite();
  const { id } = useParams();
  const history = useHistory();
  const post = (blogs || []).find(p => p.id === parseInt(id));
  const [lightbox, setLightbox] = useState({ open: false, src: '' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!post) {
    if (loading) {
       return (
         <div className="waya-blog-detail-page">
           <TopNav />
           <div style={{ height: '100px' }}></div>
           <div className="container mt-5">
             <div className="row justify-content-center">
               <div className="col-lg-10">
                 <Skeleton height={40} width="80%" className="mb-4" />
                 <Skeleton height={20} width="30%" className="mb-5" />
                 <Skeleton height={400} className="mb-5" />
                 <Skeleton count={10} />
               </div>
             </div>
           </div>
           <PageFooter />
         </div>
       );
    }
    return (
      <div className="waya-blog-detail-page">
      <div style={{ height: '100px' }}></div>
        <div className="container py-5 text-center mt-5">
          <h2>Post not found</h2>
          <button onClick={() => history.push('/blogs')} className="waya-btn-primary mt-3">
            Back to Blog
          </button>
        </div>
        <PageFooter />
      </div>
    );
  }

  const sharePost = (platform) => {
    const url = window.location.href;
    const text = post.title;
    let shareUrl = '';
    
    switch(platform) {
      case 'twitter': shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`; break;
      case 'facebook': shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`; break;
      case 'linkedin': shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`; break;
      default: break;
    }
    if (shareUrl) window.open(shareUrl, '_blank');
  };

  const scrollToSection = (idx) => {
    const el = document.getElementById(`section-${idx}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="waya-blog-detail-page">
      {/* Spacer to fix navbar overlap */}
      <div style={{ height: '100px' }}></div>
      <div className="container mt-5">
        {/* Header Section */}
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <button 
              onClick={() => history.push('/blogs')}
              className="d-flex align-items-center gap-2 mb-4 text-muted border-0 bg-transparent"
              style={{ fontSize: '14px' }}
            >
              <ArrowBack fontSize="small" /> Back to all articles
            </button>

            <Fade bottom>
              <h1 className="blog-detail-title">{post.title}</h1>
              <div className="d-flex align-items-center justify-content-between mt-4 py-3 border-top border-bottom">
                <div className="d-flex align-items-center gap-3">
                  <div className="author-avatar-sm">
                    {post.author.charAt(0)}
                  </div>
                  <div>
                    <div className="author-name-sm">{post.author}</div>
                    <div className="blog-meta-sm">{post.date} • 5 min read</div>
                  </div>
                </div>
                
                <div className="d-flex gap-2">
                  <IconButton onClick={() => sharePost('twitter')} size="small"><Twitter fontSize="small" /></IconButton>
                  <IconButton onClick={() => sharePost('facebook')} size="small"><Facebook fontSize="small" /></IconButton>
                  <IconButton onClick={() => sharePost('linkedin')} size="small"><LinkedIn fontSize="small" /></IconButton>
                </div>
              </div>

              <img 
                src={post.featuredImage} 
                alt={post.title} 
                className="blog-featured-img my-5 shadow-lg" 
              />
            </Fade>

            {/* Content Sections */}
            <div className="blog-content-body">
              {post.sections.map((section, idx) => (
                <div key={idx} id={`section-${idx}`} className="blog-content-section mb-5">
                  <Fade bottom>
                    {section.title && <h2 className="section-title mb-4">{section.title}</h2>}
                    <div 
                      className="section-text"
                      dangerouslySetInnerHTML={{ __html: section.text }}
                    />
                    {section.image && (
                      <img 
                        src={section.image} 
                        alt={section.title} 
                        className="section-img mt-4 shadow-sm"
                        onClick={() => setLightbox({ open: true, src: section.image })}
                        style={{ cursor: 'zoom-in' }}
                      />
                    )}
                  </Fade>
                </div>
              ))}
            </div>

            {/* Gallery Section */}
            {post.gallery && post.gallery.length > 0 && (
              <div className="blog-gallery-section mt-5 py-5 border-top">
                <h3 className="mb-4">Photo Gallery</h3>
                <div className="row g-3">
                  {post.gallery.map((img, idx) => (
                    <div key={idx} className="col-md-4 col-6">
                      <div className="gallery-img-container">
                        <img 
                          src={img} 
                          alt={`Gallery ${idx}`} 
                          onClick={() => setLightbox({ open: true, src: img })}
                          className="w-100 shadow-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section Navigator (Floating) */}
      {post.sections.length > 1 && (
        <div className="blog-section-navigator shadow-lg">
          <IconButton onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} size="small" title="Top">
            <KeyboardArrowUp />
          </IconButton>
          {post.sections.map((_, i) => (
             <div key={i} className="nav-dot" onClick={() => scrollToSection(i)} title={`Section ${i+1}`} />
          ))}
          <IconButton onClick={() => window.scrollTo({top: document.body.scrollHeight, behavior: 'smooth'})} size="small" title="Bottom">
            <KeyboardArrowDown />
          </IconButton>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightbox.open && (
        <div className="waya-lightbox-overlay" onClick={() => setLightbox({ open: false, src: '' })}>
          <img src={lightbox.src} alt="Enlarged view" />
          <div className="lightbox-close">✕</div>
        </div>
      )}

    </div>
  );
}
